import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { getWebSocketUrl } from "../api/client";
import { useCurrentUser } from "./CurrentUserContext";
import {
  ensureConnection,
  identify,
  subscribeToPresence,
  subscribeToPresenceOffline,
} from "../api/wsShared";
import { pickLatestLastSeen } from "../utils/lastSeen";

const PresenceContext = createContext(null);

export function usePresence() {
  const ctx = useContext(PresenceContext);
  return ctx;
}

/**
 * Provides online user IDs (Set of remote_id) from WebSocket presence.
 * Uses CurrentUserContext for identify; opens WS so this client is counted as online.
 */
export function PresenceProvider({ children }) {
  const { remoteId } = useCurrentUser();
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());
  const [offlineLastSeenByRemoteId, setOfflineLastSeenByRemoteId] = useState(
    () => new Map()
  );

  useEffect(() => {
    let cancelled = false;

    if (remoteId != null) {
      const url = getWebSocketUrl();
      ensureConnection(url);
      identify(remoteId);
    }

    const unsub = subscribeToPresence((ids) => {
      if (!cancelled) setOnlineUserIds(ids);
    });

    const unsubOffline = subscribeToPresenceOffline((userRemoteId, lastSeenAt) => {
      if (cancelled) return;
      setOfflineLastSeenByRemoteId((prev) => {
        const next = new Map(prev);
        next.set(Number(userRemoteId), lastSeenAt);
        return next;
      });
    });

    return () => {
      cancelled = true;
      unsub();
      unsubOffline();
    };
  }, [remoteId]);

  const getLastSeen = useCallback(
    (id, apiLastSeen = null) => {
      const override =
        id != null ? offlineLastSeenByRemoteId.get(Number(id)) ?? null : null;
      return pickLatestLastSeen(apiLastSeen, override);
    },
    [offlineLastSeenByRemoteId]
  );

  const value = useMemo(
    () => ({
      onlineUserIds,
      isOnline: (id) => id != null && onlineUserIds.has(Number(id)),
      getLastSeen,
    }),
    [onlineUserIds, getLastSeen]
  );

  return (
    <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>
  );
}
